import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { ATCSelector } from "@/components/ATCSelector";
import { useAudio } from "@/contexts/AudioContext";
import { supabase } from "@/integrations/supabase/client";
import { Clock } from "lucide-react";

interface ATCFeed {
  id: string;
  airport_code: string;
  airport_name: string;
  city: string;
  country: string;
  stream_url: string;
  frequency: string;
  feed_type: string;
  is_active: boolean;
}

interface ATCPanelProps {
  onAirportSelect?: (airportCode: string) => void;
}

export const ATCPanel = ({ onAirportSelect }: ATCPanelProps = {}) => {
  const { soundVolume, setSoundVolume, setAtcStream, toggleAtc, isAtcActive, atcStreamUrl } = useAudio();
  const [selectedFeed, setSelectedFeed] = useState<string>("");
  const [lastVerified, setLastVerified] = useState<Date | null>(null);
  const [nextVerification, setNextVerification] = useState<Date | null>(null);
  const [timeUntilNext, setTimeUntilNext] = useState<string>("");

  useEffect(() => {
    const fetchVerificationStatus = async () => {
      const { data } = await supabase
        .from('verification_runs')
        .select('completed_at')
        .eq('status', 'completed')
        .order('completed_at', { ascending: false })
        .limit(1)
        .single();

      if (data?.completed_at) {
        const lastRun = new Date(data.completed_at);
        setLastVerified(lastRun);
        // Next verification is 4 hours after last one
        const next = new Date(lastRun.getTime() + 4 * 60 * 60 * 1000);
        setNextVerification(next);
      }
    };

    fetchVerificationStatus();
    const interval = setInterval(fetchVerificationStatus, 60000); // Refresh every minute

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!nextVerification) return;

    const updateCountdown = () => {
      const now = new Date();
      const diff = nextVerification.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeUntilNext("Verifying now...");
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeUntilNext(`${hours}h ${minutes}m ${seconds}s`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [nextVerification]);

  const handleVolumeChange = (value: number[]) => {
    setSoundVolume(value[0]);
  };

  const handleSelectFeed = async (feed: ATCFeed) => {
    const feedName = `${feed.airport_code} - ${feed.airport_name}`;
    setSelectedFeed(feedName);
    
    // First set the stream and wait for it to be ready
    await setAtcStream(feed.stream_url, feed.airport_code);
    
    // Notify parent component of airport selection
    if (onAirportSelect) {
      onAirportSelect(feed.airport_code);
    }
    
    // Now that stream is ready, start playback if not already active
    if (!isAtcActive) {
      toggleAtc();
    }
  };

  return (
    <Card className="glass-card p-6 h-full">
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-2">Select Airport</h2>
          <p className="text-sm text-muted-foreground">
            Look for your favorite airport tower communications below. Note* certain airports may cut out time to time.
          </p>
        </div>

        <div className="space-y-4">
          <ATCSelector selectedFeedUrl={atcStreamUrl} onSelectFeed={handleSelectFeed} />
          
          <div className="bg-muted/50 rounded-lg p-3 space-y-2">
            {lastVerified ? (
              <>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span>Last verified: {lastVerified.toLocaleTimeString()}</span>
                </div>
                {timeUntilNext && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>Next verification in: {timeUntilNext}</span>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>Stream verification pending...</span>
              </div>
            )}
          </div>
          
          {selectedFeed && (
            <div className="flex items-center gap-2 text-sm">
              <div className={`h-2 w-2 rounded-full ${isAtcActive ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
              <span className="text-muted-foreground">
                {isAtcActive ? `LIVE ${selectedFeed}` : selectedFeed}
              </span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">ATC Volume</label>
            <span className="text-xs text-muted-foreground">{soundVolume}%</span>
          </div>
          <Slider
            value={[soundVolume]}
            onValueChange={handleVolumeChange}
            max={100}
            step={1}
          />
        </div>

      </div>
    </Card>
  );
};
